import { ColumnDef } from "@tanstack/react-table";

import { DataTableColumnHeader } from "../shared/data-table-column-header";
import { DataTableRowActions } from "./data-table-row-actions";
import { ClassificationBadge } from "./classification-badge";
import { ConciliationGroupBadge } from "./conciliation-group-badge";
import { PayrollVoucherStatusCell } from "./payroll-voucher-status-cell";
import {
  CLASSIFICATION_LABELS,
  CONCILIATION_GROUP_LABELS,
  PAYROLL_VOUCHER_RESPONSE_LABELS,
} from "../../utils/messages";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { StatusBadge } from "../shared/status-badge";
import { StatusWithResubmission } from "../shared/status-with-resubmission";
import { isResubmitted } from "../../utils/resubmission";
import { REVERSAL_LABELS } from "../../utils/reversal-messages";
import { STATUS_VARIANTS } from "../../utils/constants";
import { Checkbox } from "@/components/ui/checkbox";
import { canDecideRow, ReversibleRow } from "../../utils/reversal-availability";
import { useGetJustificationActiveQuery } from "@/services/justification";
import { useMemo } from "react";
import { RESUBMISSION_BADGE_TEST_ID } from "../../utils/case-history-test-ids";

export const REVERTED_BADGE_TEST_ID: string = "reverted-badge";

export const useColumns = (openCycle?: string): ColumnDef<any>[] => {
  const { data: justifications } = useGetJustificationActiveQuery();
  return useMemo(() => [
    {
      id: "select",
      header: ({ table }) => {
        const selectableRows = table
          .getRowModel()
          .rows.filter(row => canDecideRow(row.original as ReversibleRow));

        if (selectableRows.length === 0) {
          return (
            <Checkbox
              checked={false}
              disabled
              aria-label="Nenhum item selecionável"
            />
          );
        }

        const allSelected = selectableRows.every(row => row.getIsSelected());
        const someSelected = selectableRows.some(row => row.getIsSelected());

        return (
          <Checkbox
            checked={allSelected || (someSelected && "indeterminate")}
            onCheckedChange={(value) => {
              selectableRows.forEach(row => row.toggleSelected(!!value));
            }}
            aria-label="Selecionar todos"
          />
        );
      },
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Selecionar linha"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "conciliation_date",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Data de Conciliação" />
      ),
      cell: ({ row }) => {
        const date = row.original.conciliation?.date;
        if (!date) return "-";

        const formattedDate = new Date(date).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
        return formattedDate;
      },
    },
    {
      accessorKey: "store_code",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Loja" />
      ),
      cell: ({ row }) => {
        return row.original.store_code || "-";
      },
    },
    {
      accessorKey: "operator",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Operador" />
      ),
      cell: ({ row }) => {
        return row.original.operator || "-";
      },
    },
    {
      id: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Nome" />
      ),
      cell: ({ row }) => {
        return (
          <span className="whitespace-nowrap">{row.original?.name || "-"}</span>
        );
      },
    },
    {
      accessorKey: "amount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Valor" />
      ),
      cell: ({ row }) => {
        const amount: number = parseFloat(row.original.amount);
        const formattedAmount: string = new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(amount);

        const AMOUNT_CONFIG: Record<string, { bg: string; text: string; icon: string }> = {
          negative: { bg: "bg-red-50", text: "text-red-700", icon: "↓" },
          zero: { bg: "bg-gray-50", text: "text-gray-500", icon: "" },
          positive: { bg: "bg-green-50", text: "text-green-700", icon: "↑" },
        };

        const key: string = amount < 0 ? "negative" : amount === 0 ? "zero" : "positive";
        const config = AMOUNT_CONFIG[key];

        return (
          <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-semibold ${config.bg} ${config.text}`}>
            {config.icon && <span>{config.icon}</span>}
            {formattedAmount}
          </span>
        );
      },
    },
    {
      id: "analyst",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Conferente" />
      ),
      cell: ({ row }) => {
        return row.original.conciliation?.store?.responsible?.name || "-";
      },
    },

    {
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status Divergência" />
      ),
      cell: ({ row }) => {
        if (row.original.reverted) {
          return (
            <StatusBadge variant={STATUS_VARIANTS.CLOSED} data-testid={REVERTED_BADGE_TEST_ID}>
              {REVERSAL_LABELS.REVERTED_BADGE}
            </StatusBadge>
          );
        }

        return (
          <StatusWithResubmission
            status={row.original.status}
            resubmitted={isResubmitted(row.original)}
            resubmissionTestId={RESUBMISSION_BADGE_TEST_ID}
          />
        );
      },
    },
    {
      id: "conciliation_group",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={CONCILIATION_GROUP_LABELS.COLUMN_TITLE}
        />
      ),
      cell: ({ row }) => (
        <ConciliationGroupBadge
          groupId={row.original.conciliated_divergence_id}
          derivedFrom={row.original.derived_from}
        />
      ),
    },
    {
      id: "payroll_voucher_status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={PAYROLL_VOUCHER_RESPONSE_LABELS.COLUMN_TITLE}
        />
      ),
      cell: ({ row }) => <PayrollVoucherStatusCell divergence={row.original} />,
      enableSorting: false,
    },
    {
      id: "justification",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Justificativa" />
      ),
      cell: ({ row }) => {
        const justificationId = row.original.justification_id;

        if (!justificationId || !justifications) return "-";

        const justification = justifications.find((j: any) => j.id === justificationId);
        const label: string = justification ? justification.name : `ID: ${justificationId}`;

        return (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block max-w-[160px] truncate cursor-default">
                  {label}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="max-w-[300px]">{label}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      id: "is_loss",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={CLASSIFICATION_LABELS.COLUMN_TITLE}
        />
      ),
      cell: ({ row }) => <ClassificationBadge divergence={row.original} />,
    },
    {
      id: "actions",
      header: "Ações",
      cell: ({ row }) => (
        <DataTableRowActions row={row} openCycle={openCycle} />
      ),
      enableSorting: false,
    },
  ], [justifications, openCycle]);
};
